import type {
  CamposDeIntegracao,
  CredenciadoResumido,
  SituacaoDaPessoa,
} from '@/modulos/operacao/comum/contrato';
import type { ExtrasDoDetalhe } from '@/modulos/operacao/cadastros/leitura';
import type { TipoDeCredenciado } from '@/nucleo/pessoaNatureza';
import type { PresetDeVencimento } from '@/nucleo/vencimento';

/**
 * O veiculo, do lado da tela.
 *
 * O VEICULO TEM DUAS IDENTIDADES, e as duas aparecem na listagem: o CRM — a licenca
 * municipal, que e o documento do TEG e viaja no termo, na ordem de servico e na medicao —
 * e a placa, do Detran, que muda quando o veiculo e substituido e serve para conferir na
 * rua. Quem liga para reclamar cita uma das duas, nunca o nome da empresa.
 *
 * CAPACIDADE TEM TRES NUMEROS, e confundi-los tem consequencia financeira: a
 * `capacidade_detran` e o que o documento diz; a `capacidade_teg` e o que o programa
 * remunera (a regra e `capacidade_detran - 1`, e a carga do legado preservou divergencias
 * que o DTP ainda vai ratificar — docs/ALVOS.md); a `capacidade_acessivel` conta as
 * posicoes para cadeira de rodas, e depende de ancoragem instalada.
 */
/** Combustivel do veiculo, como o SGTP informa. */
export const TIPOS_DE_COMBUSTIVEL = [
  'GASOLINA',
  'ETANOL',
  'FLEX',
  'DIESEL',
  'GNV',
  'HIBRIDO',
  'ELETRICO',
  'OUTRO',
] as const;
export type TipoDeCombustivel = (typeof TIPOS_DE_COMBUSTIVEL)[number];

export const ROTULO_DO_COMBUSTIVEL: Record<TipoDeCombustivel, string> = {
  GASOLINA: 'Gasolina',
  ETANOL: 'Etanol',
  FLEX: 'Flex',
  DIESEL: 'Diesel',
  GNV: 'GNV',
  HIBRIDO: 'Híbrido',
  ELETRICO: 'Elétrico',
  OUTRO: 'Outro',
};

/**
 * Tipo de bancada — a chave FINANCEIRA de precificacao, diferente do `TipoDeVeiculo`
 * descritivo (ver o comentario de `classificarTipoDeVeiculo`). Vive na aba "Bancada &
 * Capacidade", uma vigencia por linha.
 */
export const TIPOS_DE_BANCADA = ['CONVENCIONAL', 'CRECHE', 'ACESSIVEL'] as const;
export type TipoDeBancada = (typeof TIPOS_DE_BANCADA)[number];

export const ROTULO_DO_TIPO_DE_BANCADA: Record<TipoDeBancada, string> = {
  CONVENCIONAL: 'Convencional',
  CRECHE: 'Creche',
  ACESSIVEL: 'Acessível',
};

/**
 * O veiculo como a API de leitura o devolve na listagem (`GET bff/v1/veiculos`).
 *
 * NAO HA COLUNA DE VALIDADE DA 0002 AQUI. A J-05 nao preenche `crlv_validade`,
 * `vistoria_validade`, `seguro_validade`…; a validade que vale e a da decisao que
 * aprovou o documento (`recurso_documento_decisoes`), e ela chega em `validades`.
 * `validades` e nulo quando o perfil nao le a documentacao (ver `cadastros/leitura.ts`).
 */
export interface Veiculo extends CamposDeIntegracao {
  id: string;
  /** Identificador legivel do endereco de detalhe; o id, quando o cadastro nao tem slug. */
  slug: string;
  /** Licenca municipal do veiculo. Nula enquanto o veiculo nao pode rodar no programa. */
  crm: string | null;
  /** Sem pontuacao, em maiusculas. A mascara e da tela. */
  placa: string;
  marca: string | null;
  modelo: string | null;
  ano_fabricacao: number | null;
  /** O que o programa remunera. */
  capacidade_teg: number | null;
  /** Posicoes de ancoragem para cadeira de rodas. */
  capacidade_acessivel: number | null;
  acessivel: boolean | null;
  tipo_bancada: TipoDeBancada | null;
  /** Inativacao por regra (nao a mao). O motivo inteiro fica no detalhe. */
  motivo_inativacao: string | null;
  tag_origem_inativacao: string | null;
  /** Texto da seguradora no cadastro, quando informado. */
  seguro_seguradora: string | null;
  credenciado_id: string | null;
  credenciados: Pick<CredenciadoResumido, 'id' | 'nome' | 'slug' | 'tipo'> | null;
  status: SituacaoDaPessoa;
  /** Estado do cadastro na J-05 (0063). */
  estado_cadastro: string | null;
  /** Validades aprovadas dos documentos em vigor (ISO), ou nulo sem permissao de leitura. */
  validades: string[] | null;
}

/** O veiculo no detalhe (`GET bff/v1/veiculos/:referencia`). */
export interface VeiculoDetalhe extends Veiculo, ExtrasDoDetalhe {
  cor: string | null;
  ano_modelo: number | null;
  chassi: string | null;
  renavam: string | null;
  tipo_combustivel: TipoDeCombustivel | null;
  /** O que o documento do Detran declara. */
  capacidade_detran: number | null;
  capacidade_creche: number | null;
  observacoes: string | null;
  titular_atual: { id: string; slug: string; nome: string | null } | null;
  created_at: string;
  updated_at: string;
}

/**
 * Uma apolice de seguro do veiculo — APP (Acidentes Pessoais a Passageiros), RC
 * (Responsabilidade Civil), Casco ou Compreensivo.
 */
export const TIPOS_DE_APOLICE = ['APP', 'RC', 'CASCO', 'COMPREENSIVO'] as const;
export type TipoDeApolice = (typeof TIPOS_DE_APOLICE)[number];
export const ROTULO_DA_APOLICE: Record<TipoDeApolice, string> = {
  APP: 'APP',
  RC: 'RC',
  CASCO: 'Casco',
  COMPREENSIVO: 'Compreensivo',
};

export const SITUACOES_DE_APOLICE = ['VIGENTE', 'VENCIDA', 'CANCELADA', 'SUBSTITUIDA'] as const;
export type SituacaoDeApolice = (typeof SITUACOES_DE_APOLICE)[number];
export const ROTULO_DA_SITUACAO_DE_APOLICE: Record<SituacaoDeApolice, string> = {
  VIGENTE: 'Vigente',
  VENCIDA: 'Vencida',
  CANCELADA: 'Cancelada',
  SUBSTITUIDA: 'Substituída',
};

export interface ApoliceDeVeiculo {
  id: string;
  tipo: TipoDeApolice;
  numeroApolice: string;
  seguradora: string | null;
  vigenciaInicio: string;
  vigenciaFim: string;
  status: SituacaoDeApolice;
  observacoes: string | null;
}

/** Uma vistoria do veiculo — oficial (Detran) ou interna (equipe do DTP). */
export const TIPOS_DE_VISTORIA = ['DETRAN', 'INTERNA'] as const;
export type TipoDeVistoria = (typeof TIPOS_DE_VISTORIA)[number];
export const ROTULO_DA_VISTORIA: Record<TipoDeVistoria, string> = {
  DETRAN: 'DETRAN',
  INTERNA: 'Interna (DTP)',
};

export const RESULTADOS_DE_VISTORIA = [
  'APROVADA',
  'APROVADA_COM_RESSALVAS',
  'PERMITIDA',
  'REPROVADA',
  'PENDENTE',
] as const;
export type ResultadoDeVistoria = (typeof RESULTADOS_DE_VISTORIA)[number];
export const ROTULO_DO_RESULTADO_DE_VISTORIA: Record<ResultadoDeVistoria, string> = {
  APROVADA: 'Aprovada',
  APROVADA_COM_RESSALVAS: 'Com ressalvas',
  PERMITIDA: 'Permitida',
  REPROVADA: 'Reprovada',
  PENDENTE: 'Pendente',
};

export interface VistoriaDeVeiculo {
  id: string;
  tipo: TipoDeVistoria;
  dataVistoria: string;
  validadeAte: string;
  resultado: ResultadoDeVistoria;
  ressalvas: string | null;
  observacoes: string | null;
}

/** De onde veio a vigencia de bancada — ver `docs/ALVOS.md`, "Bancada & Capacidade". */
export const ORIGENS_DE_BANCADA = [
  'OPERACIONAL',
  'MIGRACAO_TERMO',
  'REVISAO_DTP',
  'AJUSTE_TECNICO',
] as const;
export type OrigemDeBancada = (typeof ORIGENS_DE_BANCADA)[number];
export const ROTULO_DA_ORIGEM_DE_BANCADA: Record<OrigemDeBancada, string> = {
  OPERACIONAL: 'Operacional',
  MIGRACAO_TERMO: 'Migração do termo',
  REVISAO_DTP: 'Revisão DTP',
  AJUSTE_TECNICO: 'Ajuste técnico',
};

export const SITUACOES_DE_VISTORIA_DE_BANCADA = ['PENDENTE', 'APROVADA', 'REPROVADA'] as const;
export type SituacaoDeVistoriaDeBancada = (typeof SITUACOES_DE_VISTORIA_DE_BANCADA)[number];

export interface VigenciaDeBancada {
  id: string;
  tipoBancada: TipoDeBancada;
  lugaresTotais: number;
  lugaresDescontados: number;
  lugaresEfetivos: number;
  lugaresAncoragem: number | null;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  origem: OrigemDeBancada;
  statusVistoria: SituacaoDeVistoriaDeBancada;
  motivoAlteracao: string | null;
}

/** Vinculo de propriedade entre um titular e este veiculo — historico de titularidade. */
export const TIPOS_DE_VINCULO_TITULAR = ['PROPRIETARIO', 'ARRENDATARIO', 'FIDUCIARIO'] as const;
export type TipoDeVinculoTitular = (typeof TIPOS_DE_VINCULO_TITULAR)[number];
export const ROTULO_DO_VINCULO_TITULAR: Record<TipoDeVinculoTitular, string> = {
  PROPRIETARIO: 'Proprietário',
  ARRENDATARIO: 'Arrendatário',
  FIDUCIARIO: 'Fiduciário',
};

export interface VinculoDeTitularNoVeiculo {
  id: string;
  titularNome: string;
  titularCpfCnpj: string | null;
  titularSlug: string | null;
  tipoVinculo: TipoDeVinculoTitular;
  dataInicio: string;
  dataFim: string | null;
  ativo: boolean;
}

/** OS vinculada a este veiculo — texto simples, sem link (nem termo nem OS tem tela ainda). */
export interface OrdemDeServicoVinculada {
  id: string;
  numero: string;
  status: string;
  dataInicio: string;
  dataFim: string | null;
  termoNumeroProcesso: string | null;
}

/** Um evento do historico de alteracoes do veiculo — aba "Auditoria". */
export interface EventoDeAuditoriaDoVeiculo {
  id: string;
  acao: string;
  descricao: string;
  autorNome: string;
  dataHora: string;
}

/**
 * Categoria descritiva do veiculo — van, micro-onibus, onibus, veiculo adaptado.
 *
 * E PARAMETRIZADA, NAO FIXA NO CODIGO: as faixas de capacidade sao cadastradas, e mudam
 * por decisao administrativa. Uma lista fixa aqui exigiria publicacao nova a cada ajuste
 * de faixa.
 *
 * NAO CONFUNDIR COM TIPO DE BANCADA. Bancada e o que define preco; este tipo e so leitura
 * — serve para quem varre a lista reconhecer o porte do veiculo de relance.
 */
export interface TipoDeVeiculo {
  id: string;
  nome: string;
  slug: string;
  capacidade_min: number | null;
  capacidade_max: number | null;
  ordem: number;
  ativo: boolean;
}

/**
 * Em qual categoria o veiculo cai.
 *
 * ACESSIVEL TEM PRECEDENCIA quando a faixa comporta: um veiculo adaptado de doze lugares e
 * antes de tudo um veiculo adaptado — e essa e a informacao que decide se ele atende um
 * estudante em cadeira de rodas. O porte vem depois.
 *
 * Devolve `null` quando a capacidade nao esta informada ou nao cabe em faixa nenhuma. E o
 * certo: chutar a categoria mais proxima esconderia cadastro incompleto justamente onde
 * ele importa.
 */
export function classificarTipoDeVeiculo(
  veiculo: Pick<Veiculo, 'capacidade_teg' | 'acessivel'> | null | undefined,
  tipos: readonly TipoDeVeiculo[] | undefined,
): TipoDeVeiculo | null {
  if (!veiculo || !tipos || tipos.length === 0) {
    return null;
  }

  const capacidade = Number(veiculo.capacidade_teg ?? 0);
  if (!capacidade || capacidade <= 0) {
    return null;
  }

  const cabeNaFaixa = (tipo: TipoDeVeiculo) => {
    const minimo = tipo.capacidade_min ?? 0;
    const maximo = tipo.capacidade_max ?? Number.POSITIVE_INFINITY;
    return capacidade >= minimo && capacidade <= maximo;
  };

  if (veiculo.acessivel === true) {
    const adaptado = tipos.find((tipo) => tipo.slug === 'veiculo-adaptado');
    if (adaptado && cabeNaFaixa(adaptado)) {
      return adaptado;
    }
  }

  return (
    tipos
      .filter((tipo) => tipo.slug !== 'veiculo-adaptado' || veiculo.acessivel === true)
      .filter(cabeNaFaixa)
      .sort((a, b) => a.ordem - b.ordem)[0] ?? null
  );
}

/** A faixa de capacidade do tipo, em texto — vai no tooltip do selo. */
export function descreverFaixa(tipo: TipoDeVeiculo): string {
  const { capacidade_min: minimo, capacidade_max: maximo } = tipo;
  if (minimo != null && maximo != null) {
    return `${minimo}–${maximo} lugares`;
  }
  if (minimo != null) {
    return `${minimo} lugares ou mais`;
  }
  if (maximo != null) {
    return `até ${maximo} lugares`;
  }
  return 'sem faixa definida';
}

/**
 * Veiculo declarado acessivel SEM numero de ancoragens informado.
 *
 * E uma pendencia de cadastro com efeito operacional: sem saber quantas ancoragens ha, nao
 * se pode vincular o veiculo a uma rota com estudante em cadeira de rodas. Vive aqui, e nao
 * na tela, porque a listagem usa isso em dois lugares — o selo na linha e o filtro — e duas
 * copias da mesma condicao acabam divergindo.
 */
export function faltamAncoragens(
  veiculo: Pick<Veiculo, 'acessivel' | 'capacidade_acessivel'>,
): boolean {
  return veiculo.acessivel === true && (veiculo.capacidade_acessivel ?? 0) === 0;
}

export const OPCOES_DE_TIPO_DE_PESSOA: readonly { value: TipoDeCredenciado; label: string }[] = [
  { value: 'PF', label: 'Pessoa física' },
  { value: 'PJ', label: 'Pessoa jurídica' },
  { value: 'COOPERATIVA', label: 'Cooperativa' },
];

export interface FiltroDeVeiculos {
  pagina: number;
  tamanho: number;
  busca?: string;
  situacao?: string;
  /** Natureza do credenciado que opera o veiculo. */
  tipoPessoa?: string;
  /** `slug` de `TipoDeVeiculo`. O servidor resolve a faixa. */
  tipoVeiculo?: string;
  /** Validade aprovada da apolice de seguro (`APOLICE_SEGURO`, J-05). */
  seguro?: PresetDeVencimento;
  /** `true` traz so os acessiveis sem ancoragem informada. Ver `faltamAncoragens`. */
  ancoragensPendentes?: boolean;
  /** `true` traz so os que estao numa OS da jornada ATIVA e vigente hoje. */
  ativoEmOs?: boolean;
}
